<template>
  <div class="dfc-card sa-card">
    <!-- Header -->
    <div class="sa-card-header">
      <span class="sa-card-title">
        <i class="bx bx-bar-chart-square mr-2" style="color:#556ee6"></i>
        Análise por Campo
        <span v-if="selectedField" class="dfc-field-chip">{{ selectedField.label }}</span>
      </span>
      <div class="dfc-header-right">
        <div v-if="selectedField" class="dfc-mode">
          <button
            class="dfc-mode-btn"
            :class="{ active: mode === 'distribution' }"
            @click="setMode('distribution')"
          >Distribuição</button>
          <button
            class="dfc-mode-btn"
            :class="{ active: mode === 'unique' }"
            @click="setMode('unique')"
          >Valores únicos</button>
        </div>
        <div v-if="selectedField" class="dfc-filter">
          <button
            class="dfc-filter-btn"
            :class="{ active: filter === 'all' }"
            @click="setFilter('all')"
          >Inscritos</button>
          <button
            class="dfc-filter-btn dfc-filter-btn--green"
            :class="{ active: filter === 'confirmed' }"
            @click="setFilter('confirmed')"
          >Confirmados</button>
        </div>
        <button v-if="selectedField" class="dfc-change-btn" @click="openPicker" title="Trocar campo">
          <i class="bx bx-transfer-alt"></i>
        </button>
      </div>
    </div>

    <!-- Empty state: no field selected -->
    <div v-if="!selectedField" class="dfc-placeholder" @click="openPicker">
      <div class="dfc-placeholder-icon">
        <i class="bx bx-stats"></i>
      </div>
      <div class="dfc-placeholder-text">Clique para escolher um campo</div>
      <div class="dfc-placeholder-sub">Visualize a distribuição de inscritos por qualquer campo do evento</div>
    </div>

    <!-- Loading -->
    <div v-else-if="loadingChart" class="dfc-loading">
      <b-spinner small variant="primary"></b-spinner>
      <span>Carregando...</span>
    </div>

    <!-- Modo: valores únicos -->
    <div v-else-if="mode === 'unique' && chartData" class="dfc-unique" :class="{ 'is-searching': searching }">
      <div class="dfc-unique-headline">
        <span class="dfc-unique-icon" :class="{ confirmed: filter === 'confirmed' }"><i class="bx bx-fingerprint"></i></span>
        <div class="dfc-unique-headline-body">
          <div class="dfc-unique-number">{{ chartData.distinct_count.toLocaleString('pt-BR') }}</div>
          <div class="dfc-unique-label">
            valores únicos · {{ selectedField.label }}
            <span class="dfc-unique-filter-tag">{{ filter === 'confirmed' ? 'com confirmados' : 'entre inscritos' }}</span>
          </div>
        </div>
        <div class="dfc-unique-resp">{{ chartTotalFormatted }}<span>preenchidos</span></div>
      </div>

      <div v-if="chartGroups.length" class="dfc-groups">
        <div class="dfc-groups-title">Por grupo</div>
        <div class="dfc-groups-list">
          <span
            v-for="(g, gi) in chartGroups"
            :key="'ug-' + gi"
            class="dfc-group-pill"
            :class="{ 'dfc-group-pill--none': g.group_name === 'Sem grupo', 'dfc-group-pill--companion': g.is_companion }"
          >
            <i v-if="g.is_companion" class="bx bx-link-alt dfc-group-pill-ico"></i>
            <span class="dfc-group-pill-name" :title="g.group_name">{{ g.group_name }}</span>
            <span class="dfc-group-pill-count">{{ g.count.toLocaleString('pt-BR') }}</span>
          </span>
        </div>
      </div>

      <div class="dfc-unique-search">
        <i class="bx bx-search-alt-2"></i>
        <input v-model="searchTerm" @input="onSearchInput" type="text" placeholder="Buscar valor…" />
        <b-spinner v-if="searching" small variant="primary"></b-spinner>
      </div>

      <div v-if="chartData.data.length" class="dfc-unique-list dfc-thin-scroll">
        <div v-for="(item, i) in chartData.data" :key="i" class="dfc-unique-row">
          <span class="dfc-unique-val" :title="item.value">{{ item.value }}</span>
          <span class="dfc-unique-cnt">{{ item.count.toLocaleString('pt-BR') }}</span>
        </div>
      </div>
      <div v-else class="dfc-unique-empty">Nenhum valor encontrado.</div>

      <div v-if="chartData.truncated" class="dfc-unique-more">
        Mostrando os {{ chartData.data.length }} mais frequentes de {{ chartData.distinct_count.toLocaleString('pt-BR') }}. Use a busca para encontrar um valor específico.
      </div>
    </div>

    <!-- Chart -->
    <div v-else-if="chartData && chartData.data.length" class="dfc-chart-wrap">
      <div class="dfc-chart-meta">
        <span class="dfc-total-badge">
          <i class="bx bx-group"></i>
          {{ chartTotalFormatted }} {{ filter === 'confirmed' ? 'confirmados' : 'inscritos' }} com este campo
        </span>
      </div>
      <div v-if="chartGroups.length" class="dfc-groups">
        <div class="dfc-groups-title">Por grupo</div>
        <div class="dfc-groups-list">
          <span
            v-for="(g, gi) in chartGroups"
            :key="'dg-' + gi"
            class="dfc-group-pill"
            :class="{ 'dfc-group-pill--none': g.group_name === 'Sem grupo', 'dfc-group-pill--companion': g.is_companion }"
          >
            <i v-if="g.is_companion" class="bx bx-link-alt dfc-group-pill-ico"></i>
            <span class="dfc-group-pill-name" :title="g.group_name">{{ g.group_name }}</span>
            <span class="dfc-group-pill-count">{{ g.count.toLocaleString('pt-BR') }}</span>
          </span>
        </div>
      </div>
      <div class="dfc-ranking">
        <div
          v-for="(item, i) in chartData.data"
          :key="i"
          class="dfc-rank-row"
          :style="{ animationDelay: (i * 40) + 'ms' }"
        >
          <div class="dfc-rank-num" :class="rankClass(i)">{{ i + 1 }}</div>
          <div class="dfc-rank-body">
            <div class="dfc-rank-label" :title="item.value">{{ item.value }}</div>
            <div class="dfc-rank-bar-track">
              <div
                class="dfc-rank-bar-fill"
                :class="{ 'dfc-rank-bar--confirmed': filter === 'confirmed' }"
                :style="{ width: barWidth(item.count) }"
              ></div>
            </div>
          </div>
          <div class="dfc-rank-right">
            <span class="dfc-rank-count">{{ item.count.toLocaleString('pt-BR') }}</span>
            <span class="dfc-rank-pct">{{ rankPct(item.count) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- No data -->
    <div v-else-if="chartData" class="empty-state-card">
      <div class="empty-icon"><i class="bx bx-data"></i></div>
      <div class="empty-title">Sem dados para este campo</div>
      <div class="empty-desc">Nenhum participante possui este campo preenchido.</div>
    </div>

    <!-- Field picker -->
    <div v-if="showPicker" class="dfc-picker-overlay" @click.self="closePicker">
      <div class="dfc-picker">
        <div class="dfc-picker-header">
          <div class="dfc-picker-header-left">
            <i class="bx bx-search-alt-2 dfc-picker-header-icon"></i>
            <span class="dfc-picker-title">Escolher campo para análise</span>
          </div>
          <button class="dfc-picker-close" @click="closePicker"><i class="bx bx-x"></i></button>
        </div>

        <div class="dfc-picker-modes">
          <div class="dfc-picker-modes-toggle">
            <button class="dfc-mode-btn" :class="{ active: mode === 'distribution' }" @click="setMode('distribution')">Distribuição</button>
            <button class="dfc-mode-btn" :class="{ active: mode === 'unique' }" @click="setMode('unique')">Valores únicos</button>
          </div>
          <span class="dfc-picker-mode-hint">
            {{ mode === 'unique' ? 'Conta quantos valores distintos existem (ex.: CNPJs / escolas únicas) e lista cada um.' : 'Distribuição (ranking) de um campo categórico.' }}
          </span>
        </div>

        <div v-if="loadingFields" class="dfc-picker-loading">
          <b-spinner small variant="primary"></b-spinner>
          <span>Carregando campos...</span>
        </div>

        <div v-else class="dfc-picker-body">
          <!-- Standard fields -->
          <div class="dfc-group">
            <div class="dfc-group-label">
              <i class="bx bx-user"></i> Dados gerais
            </div>
            <div class="dfc-fields-grid">
              <button
                v-for="f in standardFields"
                :key="'std-' + f.name"
                class="dfc-field-btn"
                :class="{ selected: isSelected('standard', f.name) }"
                @click="selectField({ source: 'standard', id: f.name, label: f.label })"
              >
                <span class="dfc-field-btn-label">{{ f.label }}</span>
                <i v-if="isSelected('standard', f.name)" class="bx bx-check dfc-field-check"></i>
              </button>
            </div>
          </div>

          <!-- Form fields (apenas tipos categóricos) -->
          <template v-if="formFieldGroups.length">
            <div
              v-for="formGroup in formFieldGroups"
              :key="'fg-' + formGroup.form.id"
              class="dfc-group"
            >
              <div class="dfc-group-label">
                <i class="bx bx-edit-alt"></i> {{ formGroup.form.name }}
              </div>
              <div class="dfc-fields-grid">
                <button
                  v-for="f in formGroup.fields"
                  :key="'ff-' + f.id"
                  class="dfc-field-btn"
                  :class="{ selected: isSelected('form_field', f.id) }"
                  @click="selectField({ source: 'form_field', id: f.id, label: f.label })"
                >
                  <span class="dfc-field-btn-label">{{ f.label }}</span>
                  <i v-if="isSelected('form_field', f.id)" class="bx bx-check dfc-field-check"></i>
                </button>
              </div>
            </div>
          </template>

          <!-- RC custom fields -->
          <div
            v-if="rcChartFields.length"
            class="dfc-group"
          >
            <div class="dfc-group-label">
              <i class="bx bx-category"></i> {{ availableFields.registration_control.control_name || 'Controle de Inscrição' }}
            </div>
            <div class="dfc-fields-grid">
              <button
                v-for="f in rcChartFields"
                :key="'rc-' + f.rc_field"
                class="dfc-field-btn"
                :class="{ selected: isSelected('rc_field', f.rc_field) }"
                @click="selectField({ source: 'rc_field', id: f.rc_field, label: f.label })"
              >
                <span class="dfc-field-btn-label">{{ f.label }}</span>
                <i v-if="isSelected('rc_field', f.rc_field)" class="bx bx-check dfc-field-check"></i>
              </button>
            </div>
          </div>
        </div>
        <div v-if="selectedField" class="dfc-picker-footer">
          <button class="dfc-clear-btn" @click="clearField">
            <i class="bx bx-trash"></i> Limpar seleção
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFieldDistribution, getAvailableFields } from '../services'

// Tipos de campo que produzem valores categóricos úteis para análise de distribuição
const CHARTABLE_TYPES = ['select', 'radio', 'checkbox', 'multiselect']

const CACHE_KEY = 'dfc_selected_field'
const MODE_KEY = 'dfc_mode'

export default {
  name: 'DynamicFieldChart',
  props: {
    period: { type: String, default: 'all' },
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null }
  },
  data () {
    const cached = (() => {
      try { return JSON.parse(localStorage.getItem(CACHE_KEY)) } catch { return null }
    })()
    const cachedMode = (() => {
      try { return localStorage.getItem(MODE_KEY) || 'distribution' } catch { return 'distribution' }
    })()
    return {
      selectedField: cached,
      filter: 'all',
      mode: cachedMode,
      searchTerm: '',
      searching: false,
      searchDebounce: null,
      chartData: null,
      loadingChart: false,
      showPicker: false,
      availableFields: null,
      loadingFields: false
    }
  },
  watch: {
    period () { this.loadChart() },
    dateFrom () { this.loadChart() },
    dateTo () { this.loadChart() }
  },
  mounted () {
    if (this.selectedField) this.loadChart()
  },
  computed: {
    standardFields () {
      return [
        { name: 'gender', label: 'Gênero', icon: 'bx bx-user-circle' },
        { name: 'state', label: 'Estado', icon: 'bx bx-map' },
        { name: 'city', label: 'Cidade', icon: 'bx bx-buildings' }
      ]
    },

    // Distribuição: só campos categóricos (select/radio/checkbox/multiselect).
    // Valores únicos: qualquer campo (texto, cnpj, e-mail… alta cardinalidade).
    formFieldGroups () {
      if (!this.availableFields?.form_fields?.length) return []
      const onlyChartable = this.mode !== 'unique'
      return this.availableFields.form_fields
        .map(group => ({
          ...group,
          fields: group.fields.filter(f => !onlyChartable || CHARTABLE_TYPES.includes(f.type))
        }))
        .filter(group => group.fields.length > 0)
    },

    rcChartFields () {
      if (!this.availableFields?.registration_control?.fields) return []
      // Em valores únicos, identifier/name (CNPJ/escola) são os candidatos principais.
      const exclude = this.mode === 'unique'
        ? ['group', 'tag_bundle']
        : ['identifier', 'name', 'group', 'tag_bundle']
      return this.availableFields.registration_control.fields.filter(
        f => f.rc_field && !exclude.includes(f.rc_field)
      )
    },

    chartTotal () {
      return this.chartData?.data?.reduce((s, d) => s + d.count, 0) || 1
    },

    chartRespondents () {
      return this.chartData?.respondents ?? this.chartTotal
    },

    chartTotalFormatted () {
      const n = this.chartRespondents
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toLocaleString('pt-BR')
    },

    chartMax () {
      return this.chartData?.data?.[0]?.count || 1
    },

    // Quebra por grupo (setting_groups) dos inscritos com este campo preenchido.
    // Vem do backend já ordenada por contagem desc. "Sem grupo" = group_id null.
    chartGroups () {
      return this.chartData?.groups || []
    }
  },
  methods: {
    rankClass (i) {
      if (i === 0) return 'dfc-rank-num--gold'
      if (i === 1) return 'dfc-rank-num--silver'
      if (i === 2) return 'dfc-rank-num--bronze'
      return ''
    },

    barWidth (count) {
      return Math.max(2, Math.round(count / this.chartMax * 100)) + '%'
    },

    rankPct (count) {
      return Math.round(count / this.chartRespondents * 100)
    },

    openPicker () {
      this.showPicker = true
      if (!this.availableFields) this.loadFields()
    },

    closePicker () {
      this.showPicker = false
    },

    async loadFields () {
      this.loadingFields = true
      try {
        const res = await getAvailableFields()
        this.availableFields = res.data
      } catch (e) {
        console.error('Erro ao carregar campos:', e)
      } finally {
        this.loadingFields = false
      }
    },

    selectField (field) {
      this.selectedField = field
      this.searchTerm = ''
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(field)) } catch {}
      this.closePicker()
      this.loadChart()
    },

    setMode (val) {
      if (this.mode === val) return
      this.mode = val
      this.searchTerm = ''
      try { localStorage.setItem(MODE_KEY, val) } catch {}
      if (this.selectedField) this.loadChart()
    },

    onSearchInput () {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => this.loadChart({ search: true }), 350)
    },

    clearField () {
      this.selectedField = null
      this.chartData = null
      try { localStorage.removeItem(CACHE_KEY) } catch {}
      this.closePicker()
    },

    setFilter (val) {
      if (this.filter === val) return
      this.filter = val
      if (this.selectedField) this.loadChart()
    },

    isSelected (source, id) {
      return this.selectedField &&
        this.selectedField.source === source &&
        String(this.selectedField.id) === String(id)
    },

    async loadChart (opts = {}) {
      if (!this.selectedField) return
      const isSearch = !!opts.search
      // Busca: reload leve (mantém o headline visível); troca de campo/modo: reload cheio
      if (isSearch) {
        this.searching = true
      } else {
        this.loadingChart = true
        this.chartData = null
      }
      try {
        const params = {
          field_source: this.selectedField.source,
          field_id: this.selectedField.id,
          filter: this.filter,
          mode: this.mode
        }
        if (this.mode === 'unique' && this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.period === 'custom' && this.dateFrom && this.dateTo) {
          params.start_date = this.dateFrom
          params.end_date = this.dateTo
        } else {
          params.period = this.period
        }
        const res = await getFieldDistribution(params)
        this.chartData = res.data
      } catch (e) {
        console.error('Erro ao carregar distribuição:', e)
      } finally {
        if (isSearch) this.searching = false
        else this.loadingChart = false
      }
    }
  }
}
</script>

<style scoped>
/* Override sa-card constraints — sem scroll, altura livre */
.dfc-card {
  min-height: unset !important;
  max-height: none !important;
  overflow: visible !important;
  overflow-y: visible !important;
}

/* === SA-CARD inner styles === */
.sa-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 8px;
}

.sa-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}

.empty-state-card .empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #b0b5c8;
  font-size: 22px;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 12px;
  color: #8b90a5;
}

/* === FIELD CHIP (selected field indicator) === */
.dfc-field-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  background: rgba(85, 110, 230, 0.08);
  border: 1px solid rgba(85, 110, 230, 0.2);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #556ee6;
  margin-left: 2px;
}

/* === HEADER RIGHT === */
.dfc-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dfc-filter {
  display: flex;
  gap: 2px;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
}

.dfc-filter-btn {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  background: transparent;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.dfc-filter-btn.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.dfc-filter-btn--green.active {
  color: #34c38f;
}

/* === MODE TOGGLE === */
.dfc-mode {
  display: flex;
  gap: 2px;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
}

.dfc-mode-btn {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  background: transparent;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}

.dfc-mode-btn.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* === PICKER MODE BAR === */
.dfc-picker-modes {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid #f1f3f5;
  background: #fff;
}

.dfc-picker-modes-toggle {
  display: flex;
  gap: 2px;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
  flex-shrink: 0;
}

.dfc-picker-mode-hint {
  font-size: 11.5px;
  color: #8b90a5;
  line-height: 1.4;
}

/* === UNIQUE VALUES MODE === */
.dfc-unique.is-searching .dfc-unique-list { opacity: 0.5; }

.dfc-unique-headline {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #f5f6ff, #eef1fc);
  border: 1px solid #e3e7fb;
  border-radius: 12px;
  margin-bottom: 14px;
}

.dfc-unique-icon {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(85,110,230,0.12);
  color: #556ee6;
  font-size: 24px;
}

.dfc-unique-icon.confirmed { background: rgba(52,195,143,0.12); color: #34c38f; }

.dfc-unique-headline-body { flex: 1; min-width: 0; }
.dfc-unique-number { font-size: 30px; font-weight: 800; color: #1a1d26; line-height: 1; }
.dfc-unique-label { font-size: 12.5px; color: #6b7280; margin-top: 4px; }

.dfc-unique-filter-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 7px;
  border-radius: 99px;
  background: rgba(85,110,230,0.1);
  color: #556ee6;
  font-size: 11px;
  font-weight: 600;
}

.dfc-unique-resp {
  text-align: right;
  font-size: 16px;
  font-weight: 700;
  color: #4a5068;
  flex-shrink: 0;
}

.dfc-unique-resp span {
  display: block;
  font-size: 10.5px;
  font-weight: 500;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dfc-unique-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  margin-bottom: 10px;
}

.dfc-unique-search i { font-size: 16px; color: #8b90a5; }
.dfc-unique-search input { flex: 1; border: none; outline: none; font-size: 13px; color: #1a1d26; background: transparent; }

.dfc-unique-list {
  display: flex;
  flex-direction: column;
  max-height: 340px;
  overflow-y: auto;
}

.dfc-unique-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f4f5f8;
}

.dfc-unique-row:last-child { border-bottom: none; }

.dfc-unique-val {
  font-size: 13px;
  color: #1a1d26;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dfc-unique-cnt { font-size: 13px; font-weight: 700; color: #556ee6; flex-shrink: 0; }
.dfc-unique-empty { padding: 28px; text-align: center; color: #8b90a5; font-size: 13px; }

.dfc-unique-more {
  margin-top: 10px;
  padding: 8px 12px;
  background: #fafbfc;
  border-radius: 6px;
  font-size: 11.5px;
  color: #8b90a5;
  text-align: center;
}

.dfc-thin-scroll::-webkit-scrollbar { width: 6px; }
.dfc-thin-scroll::-webkit-scrollbar-thumb { background: #d8dce6; border-radius: 4px; }
.dfc-thin-scroll::-webkit-scrollbar-track { background: transparent; }

.dfc-change-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e8eaf0;
  border-radius: 6px;
  background: #fff;
  color: #8b90a5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: color 0.15s, border-color 0.15s;
}

.dfc-change-btn:hover {
  color: #556ee6;
  border-color: #556ee6;
}

/* === PLACEHOLDER === */
.dfc-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  border: 2px dashed #d0d4e8;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.dfc-placeholder:hover {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.03);
}

.dfc-placeholder-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(85, 110, 230, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.dfc-placeholder-icon i {
  font-size: 26px;
  color: #556ee6;
}

.dfc-placeholder-text {
  font-size: 14px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 6px;
}

.dfc-placeholder-sub {
  font-size: 12px;
  color: #8b90a5;
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
}

/* === LOADING === */
.dfc-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px;
  color: #8b90a5;
  font-size: 13px;
}

/* === CHART META === */
.dfc-chart-wrap { }

.dfc-chart-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.dfc-total-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  background: #f5f6fa;
  color: #4a5068;
}

.dfc-total-badge i { color: #8b90a5; }

/* === GROUP BREAKDOWN (por grupo) === */
.dfc-groups {
  margin-bottom: 14px;
}

.dfc-groups-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
  margin-bottom: 8px;
}

.dfc-groups-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dfc-group-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 4px 3px 10px;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 99px;
  max-width: 100%;
}

.dfc-group-pill-name {
  font-size: 12px;
  font-weight: 600;
  color: #4a5068;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

.dfc-group-pill-ico {
  font-size: 13px;
  color: #8b90a5;
  margin-left: 2px;
  flex-shrink: 0;
}

/* Acompanhante: tint distinto pra escanear "{grupo} - {grupo do titular}". */
.dfc-group-pill--companion {
  background: rgba(85, 110, 230, 0.06);
  border-color: rgba(85, 110, 230, 0.2);
}

.dfc-group-pill--companion .dfc-group-pill-ico { color: #556ee6; }

.dfc-group-pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 1px 7px;
  background: #556ee6;
  color: #fff;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.dfc-group-pill--none .dfc-group-pill-name { color: #8b90a5; font-style: italic; }
.dfc-group-pill--none .dfc-group-pill-count { background: #b0b5c8; }

/* === RANKING CHART === */
@keyframes rankSlideIn {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}

.dfc-ranking {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dfc-rank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  animation: rankSlideIn 0.3s ease both;
}

.dfc-rank-num {
  width: 26px;
  height: 26px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f5f6fa;
  color: #b0b5c8;
}

.dfc-rank-num--gold   { background: #fef3c7; color: #d97706; }
.dfc-rank-num--silver { background: #f1f5f9; color: #64748b; }
.dfc-rank-num--bronze { background: #fef0e6; color: #c2692c; }

.dfc-rank-body {
  flex: 1;
  min-width: 0;
}

.dfc-rank-label {
  font-size: 13px;
  font-weight: 500;
  color: #1a1d26;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dfc-rank-bar-track {
  height: 6px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
}

.dfc-rank-bar-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #556ee6, #7c8ff5);
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.dfc-rank-bar--confirmed {
  background: linear-gradient(90deg, #34c38f, #56d9a8);
}

.dfc-rank-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 60px;
}

.dfc-rank-count {
  font-size: 13px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.2;
}

.dfc-rank-pct {
  font-size: 11px;
  font-weight: 500;
  color: #8b90a5;
}

/* === PICKER OVERLAY === */
.dfc-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(26, 29, 38, 0.4);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dfc-picker {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 60px rgba(26, 29, 38, 0.22);
  width: 560px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dfc-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #f1f3f5;
  flex-shrink: 0;
  background: #fafbfc;
}

.dfc-picker-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dfc-picker-header-icon {
  font-size: 18px;
  color: #556ee6;
}

.dfc-picker-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1d26;
}

.dfc-picker-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f5f6fa;
  border: none;
  font-size: 20px;
  color: #8b90a5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.15s, color 0.15s;
}

.dfc-picker-close:hover {
  background: #eaecf2;
  color: #1a1d26;
}

.dfc-picker-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #8b90a5;
  font-size: 13px;
}

.dfc-picker-body {
  overflow-y: auto;
  padding: 20px 24px 24px;
}

.dfc-picker-footer {
  padding: 12px 24px;
  border-top: 1px solid #f1f3f5;
  flex-shrink: 0;
}

.dfc-clear-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: 1.5px dashed #e0b4b4;
  border-radius: 5px;
  background: #fff8f8;
  color: #c0392b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.dfc-clear-btn:hover {
  background: #fef0f0;
  border-color: #c0392b;
}

/* === FIELD GROUPS === */
.dfc-group {
  margin-bottom: 24px;
}

.dfc-group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #8b90a5;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;
}

.dfc-group-label i {
  font-size: 14px;
  color: #b0b5c8;
}

/* 2-column grid */
.dfc-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.dfc-field-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1.5px solid #eaecf2;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #4a5068;
  transition: background 0.12s, border-color 0.12s, color 0.12s, box-shadow 0.12s;
  text-align: left;
  position: relative;
}

.dfc-field-btn:hover {
  background: #f5f6ff;
  border-color: #c5caf5;
  color: #1a1d26;
  box-shadow: 0 2px 8px rgba(85,110,230,0.08);
}

.dfc-field-btn.selected {
  background: rgba(85, 110, 230, 0.07);
  border-color: rgba(85, 110, 230, 0.35);
  color: #3a4fa3;
  box-shadow: 0 0 0 3px rgba(85,110,230,0.08);
}

.dfc-field-btn-label {
  flex: 1;
  font-weight: 500;
  line-height: 1.3;
}

.dfc-field-check {
  font-size: 16px;
  color: #556ee6;
  flex-shrink: 0;
}
</style>
